import { useRef, type ReactNode, type RefObject } from "react";
import { styled } from "@pigment-css/react";
import { BookmarkCheck } from "@acto/web-common/components";
import { Header, MetaLabel } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Toast } from "@acto/taskpane/field-sales-2/components/primitives/Toast";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";
import {
  DrillGuideTopBar,
  OverlayBody,
  OverlayCard,
  OverlayFooter,
  OverlayFrame,
  ScrollArea,
  ScrollEndProbe,
  useScrolledToEnd,
} from "./drillOverlayChrome";
import { TalkingPointDrillNav, type DrillLevel } from "./TalkingPointDrillNav";

const TOP_BAR_SLOT_ID = "drillOverlayTopBar";
const TOP_BAR_ACTION_SLOT_ID = "drillOverlayTopBarAction";

const Subheader = styled("div")(({ theme }) => ({
  paddingInline: theme.fieldSalesTwo.spacing[16],
  paddingBottom: theme.fieldSalesTwo.spacing[12],
}));

interface DrillOverlayProps {
  eyebrow?: string;
  headline?: ReactNode;
  stack: Pick<DrillLevel, "name">[];
  onDrillTo: (depth: number) => void;
  scrollAreaRef?: RefObject<HTMLDivElement | null>;
  topBarAction?: ReactNode;
  subheader?: ReactNode;
  /** A new `id` replays the toast, even when `message` is unchanged. */
  confirmation?: { id: number; message: string } | null;
  onConfirmationClose?: () => void;
  children: ReactNode;
}

export function DrillOverlay({
  eyebrow,
  headline,
  stack,
  onDrillTo,
  scrollAreaRef,
  topBarAction,
  subheader,
  confirmation,
  onConfirmationClose,
  children,
}: DrillOverlayProps) {
  const ownScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = scrollAreaRef ?? ownScrollRef;
  const { probeRef, atEnd } = useScrolledToEnd(scrollRef);

  return (
    <OverlayFrame>
      <DrillGuideTopBar id={TOP_BAR_SLOT_ID} />
      {topBarAction && (
        <ShellSlotContent slot="topBarTrailing" id={TOP_BAR_ACTION_SLOT_ID} visibleDuringOverlay>
          {topBarAction}
        </ShellSlotContent>
      )}
      <OverlayCard bleed data-at-end={atEnd}>
        <OverlayBody>
          <ScrollArea ref={scrollRef}>
            {(eyebrow || headline) && (
              <Header data-has-headline={!!headline} data-has-subheader={!!subheader}>
                {eyebrow && <MetaLabel>{eyebrow}</MetaLabel>}
                {headline}
              </Header>
            )}
            {subheader && <Subheader>{subheader}</Subheader>}
            {children}
            <ScrollEndProbe ref={probeRef} aria-hidden="true" />
          </ScrollArea>
          <OverlayFooter>
            {confirmation ? (
              <Toast
                key={confirmation.id}
                open
                message={confirmation.message}
                onClose={() => onConfirmationClose?.()}
                icon={<BookmarkCheck aria-hidden="true" />}
                tintMessage
              />
            ) : (
              <TalkingPointDrillNav stack={stack} onDrillTo={onDrillTo} />
            )}
          </OverlayFooter>
        </OverlayBody>
      </OverlayCard>
    </OverlayFrame>
  );
}
